// MC review drawer: PENDING above HANDLED. Confirming removes the person from every
// matching session in the range (leaving needs-cover gaps) and sends the acknowledgement
// push; declining keeps them on the plan and notifies them.
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { confirmMCRequest, declineMCRequest, fetchMCRequests } from "@/lib/api/work-plan";
import { subscribeWorkPlanEvents } from "@/lib/realtime/chat-realtime-service";
import { sessionLabelFor } from "@/lib/work-plan-sessions";
import { IdentityAvatar } from "./planner-bits";
import { formatDayLabel, workRoleLabel } from "./work-plan-utils";

function rangeLabel(request) {
  return request.fromDate === request.toDate
    ? formatDayLabel(request.fromDate)
    : `${formatDayLabel(request.fromDate)} → ${formatDayLabel(request.toDate)}`;
}

// `request.sessions` is the wire value ("full" | "am" | "pm"); the word comes from the
// configured session labels so an org that renamed AM to "Morning" reads it that way here.
function sessionsLabel(request, sessions) {
  if (request.sessions === "full") return "Full day";
  return `${sessionLabelFor(sessions, request.sessions)} session only`;
}

function submittedLabel(value) {
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toRelative({ style: "short" }) : "";
}

function firstNameOf(request) {
  return String(request.staffName || "").split(/\s+/)[0] || "them";
}

function MCCard({ request, canManage, busyId, roleView, sessions, onConfirm, onDecline }) {
  const isPending = request.status === "pending";
  const busy = busyId === request.id;
  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card px-3 py-2.5",
        isPending && "border-rose-200 bg-rose-50/40 dark:border-rose-900 dark:bg-rose-950/20",
      )}
    >
      <div className="flex items-center gap-2.5">
        <IdentityAvatar id={request.staffId} name={request.staffName} size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-tight">{request.staffName || "Unknown"}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {workRoleLabel({ workRole: request.workRole, isDoctor: request.userType === "doctor" }, roleView)}
            {request.submittedAt ? ` · submitted ${submittedLabel(request.submittedAt)}` : ""}
          </p>
        </div>
        {!isPending ? (
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[11px] font-semibold",
              request.status === "confirmed"
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                : "bg-muted text-muted-foreground",
            )}
          >
            {request.status === "confirmed" ? "Confirmed" : "Declined"}
          </span>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
          MC
        </span>
        <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground/70">{rangeLabel(request)}</span>
        <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground/70">{sessionsLabel(request, sessions)}</span>
      </div>
      {request.note ? (
        <p className="mt-2 rounded-lg bg-muted/50 px-2.5 py-2 text-xs leading-relaxed text-foreground/80">{request.note}</p>
      ) : null}
      {isPending && canManage ? (
        <div className="mt-2.5 flex gap-2">
          <Button type="button" size="sm" disabled={busy} className="[&_svg]:size-3.5" onClick={() => onConfirm(request)}>
            <Check />
            Confirm absence
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => onDecline(request)}>
            Decline
          </Button>
        </div>
      ) : null}
      {request.status === "confirmed" && request.ackSentAt ? (
        <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-emerald-700 dark:text-emerald-400">
          <Check className="size-3.5" />
          Acknowledgement sent to {firstNameOf(request)} · {submittedLabel(request.ackSentAt)}
          {request.removedPostings > 0
            ? ` · removed from ${request.removedPostings} session${request.removedPostings === 1 ? "" : "s"}`
            : ""}
        </p>
      ) : null}
    </div>
  );
}

export default function MCDrawer({ open, onOpenChange, canManage, roleView, sessions, onDecided }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setIsLoading(true);
    fetchMCRequests({ status: "all" })
      .then((response) => {
        if (cancelled) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        toast.error(error instanceof Error ? error.message : "Failed to load MC requests.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, reloadKey]);

  // A new submission while the drawer is open should appear live.
  useEffect(() => {
    if (!open) return undefined;
    return subscribeWorkPlanEvents((detail) => {
      if (String(detail?.action || "").startsWith("mc_")) {
        setReloadKey((key) => key + 1);
      }
    });
  }, [open]);

  const decide = async (request, kind) => {
    setBusyId(request.id);
    try {
      const call = kind === "confirm" ? confirmMCRequest : declineMCRequest;
      const response = await call({ mcId: request.id });
      setItems((current) => current.map((item) => (item.id === request.id ? response.request : item)));
      if (kind === "confirm") {
        const removed = Number(response?.removedPostings) || 0;
        toast.success("MC confirmed", {
          description: `${request.staffName} removed from ${removed} session${removed === 1 ? "" : "s"} · acknowledgement sent.`,
        });
      } else {
        toast("MC declined", {
          description: `${request.staffName} was notified and stays on the plan.`,
        });
      }
      onDecided?.(response);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "The MC decision failed.");
      setReloadKey((key) => key + 1);
    } finally {
      setBusyId("");
    }
  };

  const pending = items.filter((item) => item.status === "pending");
  const handled = items.filter((item) => item.status !== "pending");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[400px] flex-col gap-0 p-0 sm:max-w-[400px]">
        <SheetHeader className="border-b border-border px-4 py-3.5">
          <SheetTitle className="text-sm">Medical certificates</SheetTitle>
          <SheetDescription className="text-xs">
            {pending.length} waiting for confirmation. Confirming removes the person from the plan and flags the gap.
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="min-h-0 flex-1" viewportClassName="px-4 py-3">
          <div className="flex flex-col gap-2.5">
            {isLoading && items.length === 0 ? (
              <p className="py-10 text-center text-xs text-muted-foreground">Loading…</p>
            ) : null}
            {!isLoading && items.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border py-10 text-center text-xs text-muted-foreground">
                No MC submissions.
              </p>
            ) : null}
            {pending.length > 0 ? (
              <p className="text-[10px] font-bold tracking-widest text-rose-600">PENDING · {pending.length}</p>
            ) : null}
            {pending.map((request) => (
              <MCCard
                key={request.id}
                request={request}
                canManage={canManage}
                busyId={busyId}
                roleView={roleView}
                sessions={sessions}
                onConfirm={(item) => decide(item, "confirm")}
                onDecline={(item) => decide(item, "decline")}
              />
            ))}
            {handled.length > 0 ? (
              <p className="mt-1 text-[10px] font-bold tracking-widest text-muted-foreground">HANDLED</p>
            ) : null}
            {handled.map((request) => (
              <MCCard key={request.id} request={request} canManage={canManage} busyId={busyId} roleView={roleView} sessions={sessions} onConfirm={() => {}} onDecline={() => {}} />
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
